import React, { useState } from "react";
import styles from "../incomeGridDetails.module.css";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MenuItem, TextField } from "@mui/material";
import { Info } from "react-feather";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import dayjs from "dayjs";
import NumberFormat from "react-number-format";
import { toast } from "react-smart-toaster";
import { getRefundInfo } from "../components/RefundParts";

const MoneyField = (props) => {
  return (
    <NumberFormat
      prefix=""
      displayType="input"
      type="text"
      thousandSeparator={true}
      allowNegative={false}
      decimalScale={2}
      fixedDecimalScale
      {...props}
    />
  );
};

const REASONS = [
  { value: "overpayment", label: "Overpayment" },
  { value: "duplicate", label: "Duplicate payment" },
  { value: "tenant", label: "Requested by tenant" },
  { value: "moveout", label: "Move-out / deposit return" },
  { value: "error", label: "Billing error" },
  { value: "other", label: "Other" },
];

const METHODS = {
  us_bank_account: "ACH Transfer",
  card: "Card Payment",
  cashapp: "Cash App Pay",
  klarna: "Pay By Klarna",
};

const money = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0);

const toNumber = (value) => parseFloat(String(value || "").replace(/,/g, "")) || 0;

/*
 * Record Refund: record-only. PropertyDek does not send money back; the manager refunds
 * the tenant first and records it here, which updates the payment row, ledger and balance.
 * With `refund` set it edits that refund instead.
 */
const RecordRefund = ({ rent_roll_id, item, refund, rentRollData, setModalShow, setLoading, setSnackbarMsg, setSnackbarOpen, onSaved }) => {
  const { getAccessTokenSilently } = useAuth0();
  const info = getRefundInfo(item);
  const isEdit = !!refund;
  // Editing frees up that refund's own amount
  const max = Math.round((info.remaining + (isEdit ? parseFloat(refund.amount) || 0 : 0)) * 100) / 100;
  const isOnline = item?.payment_type === "online-payment";

  // A refund normally pays back what the tenant is owed, e.g. the move-out refund out of the deposit credit
  const owed = (parseFloat(rentRollData?.balance) || 0) < 0 ? Math.abs(parseFloat(rentRollData.balance)) : 0;
  const suggested = owed > 0 && owed < max ? owed : max;
  const startAmount = isEdit ? parseFloat(refund.amount) : suggested;

  const [mode, setMode] = useState(startAmount < max ? "partial" : "full");
  const [amount, setAmount] = useState(startAmount.toFixed(2));
  const [refundedOn, setRefundedOn] = useState(isEdit ? dayjs(refund.refunded_on) : dayjs());
  const [reason, setReason] = useState(isEdit ? refund.reason : (item?.payment_type === "add-credit" ? "moveout" : "overpayment"));
  const [memo, setMemo] = useState(isEdit ? (refund.memo || "") : "");
  const [errors, setErrors] = useState({});

  const refundAmount = mode === "full" ? max : Math.min(toNumber(amount), max);
  // A refund adds the money back to what the tenant owes
  const balance = parseFloat(rentRollData?.balance) || 0;
  const balanceAfter = Math.round((balance + refundAmount - (isEdit ? parseFloat(refund.amount) || 0 : 0)) * 100) / 100;

  const method = isOnline ? (METHODS[item?.payment_details?.payment_method] || "Online Payment") : (item?.payment_type === "add-credit" ? "Credit Applied" : "Payment Sent");

  const pickMode = (newMode) => {
    setMode(newMode);
    setAmount((newMode === "full" ? max : suggested).toFixed(2));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    const value = mode === "full" ? max : toNumber(amount);
    if (value <= 0) newErrors.amount = "This field should be greater than $0.";
    else if (value > max) newErrors.amount = "The refund can't be more than " + money(max) + ".";
    if (!refundedOn || !dayjs(refundedOn).isValid()) newErrors.refunded_on = "This field is Required";
    if (!reason) newErrors.reason = "This field is Required";
    if (!memo.trim()) newErrors.memo = "This field is Required";
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setLoading(true);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    const url = process.env.REACT_APP_PAYMENT_API_URL + "v1/rent-rolls/" + rent_roll_id + "/list/" + item?.id + "/refunds" + (isEdit ? "/" + refund.id : "");
    const data = { amount: value, refunded_on: dayjs(refundedOn).format("YYYY-MM-DD"), reason, memo: memo.trim() };

    return axios({ method: isEdit ? "put" : "post", url, data, headers: { Authorization: `Bearer ${token}` } }).then((response) => {
      setLoading(false);
      setModalShow(false);
      setSnackbarOpen(true);
      setSnackbarMsg(response.data.message);
      onSaved();
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  };

  return (
    <div className={`${styles.AddSection}`}>
      <div className={`${styles.RefundInfoBox}`}>
        <Info />
        <span>
          <strong>{isOnline ? "Online payment — record only." : "Manual payment — record only."}</strong>{" "}
          {isOnline
            ? "PropertyDek does not send money back. Refund the tenant first, then record it here to update the ledger and balance."
            : "PropertyDek does not send money back. Pay the tenant back yourself first, then record it here to update the ledger and balance."}
        </span>
      </div>

      <div className={`${styles.DetailsDiv}`}>
        <div className={`${styles.DetailsDivHalf}`}>
          <p className={`${styles.DetailsTitle}`}>Current Balance</p>
          <p className={`${styles.DetailsDes}`}>
            <span>${rentRollData?.formatted_balance}</span>{" "}
            {balance > 0 ? "Outstanding" : balance < 0 ? "Over Paid" : "Balance"}
          </p>
        </div>
        <div className={`${styles.DetailsDivHalf}`}>
          <p className={`${styles.DetailsTitle}`}>Renters</p>
          <p className={`${styles.DetailsDes}`}>{rentRollData?.tenant_name}</p>
        </div>
        <div className={`${styles.RefundingRow}`}>
          <span>Refunding: <strong>{item?.description} · {dayjs(item?.date).format("MMM D")} · {method}</strong></span>
          <span className={`${styles.RefundMax}`}>Max {money(max)}</span>
        </div>
      </div>

      <form className={`${styles.AddLayoutForm}`} onSubmit={onSubmit}>
        <div className={`${styles.SMSmallSideBody}`}>
          <div className={`${styles.AddUserMain}`}>
            <div className={`${styles.RefundToggle}`} role="group" aria-label="Refund type">
              <button type="button" aria-pressed={mode === "full"} className={mode === "full" ? `${styles.RefundToggleBtn} ${styles.RefundToggleActive}` : styles.RefundToggleBtn} onClick={() => pickMode("full")}>Full refund</button>
              <button type="button" aria-pressed={mode === "partial"} className={mode === "partial" ? `${styles.RefundToggleBtn} ${styles.RefundToggleActive}` : styles.RefundToggleBtn} onClick={() => pickMode("partial")}>Partial refund</button>
            </div>

            <div className={`${styles.FormRow}`}>
              <div className={`${styles.FormGroupFull} FormGroup`}>
                <TextField
                  label="Refund Amount*"
                  variant="outlined"
                  value={mode === "full" ? max.toFixed(2) : amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{ inputComponent: MoneyField, readOnly: mode === "full" }}
                />
                {errors.amount && <p className={`${styles.ErrorM}`}>{errors.amount}</p>}
              </div>
            </div>

            <div className={`${styles.FormRow}`}>
              <div className={`${styles.FormGroupFull} FormGroup`}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Refunded on*"
                    value={refundedOn}
                    onChange={(value) => setRefundedOn(value)}
                    maxDate={dayjs()}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
                {errors.refunded_on && <p className={`${styles.ErrorM}`}>{errors.refunded_on}</p>}
              </div>
            </div>

            <div className={`${styles.FormRow}`}>
              <div className={`${styles.FormGroupFull} FormGroup`}>
                <TextField select label="Reason*" variant="outlined" value={reason} onChange={(e) => setReason(e.target.value)}>
                  {REASONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                </TextField>
                {errors.reason && <p className={`${styles.ErrorM}`}>{errors.reason}</p>}
              </div>
            </div>

            <div className={`${styles.FormRow}`}>
              <div className={`${styles.FormGroupFull} FormGroup`}>
                <TextField
                  label="Memo for your renters*"
                  variant="outlined"
                  multiline
                  rows={3}
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
                <p className={`${styles.Example}`}>
                  {100 - memo.length === 1 ? "1 character remaining" : (100 - memo.length) + " characters remaining"}
                </p>
                {errors.memo && <p className={`${styles.ErrorM}`}>{errors.memo}</p>}
              </div>
            </div>

            <div className={`${styles.RefundAfter}`}>
              <span>Balance after refund</span>
              {balanceAfter < 0 && <strong className={`${styles.RefundAfterCredit}`}>-{money(Math.abs(balanceAfter))} Over Paid</strong>}
              {balanceAfter === 0 && <strong className={`${styles.RefundAfterCredit}`}>$0.00 Cleared</strong>}
              {balanceAfter > 0 && <strong className={`${styles.RefundAfterDue}`}>{money(balanceAfter)} Balance Due</strong>}
            </div>
          </div>
        </div>
        <div className={`${styles.ButtonDiv}`}>
          <button type="button" className="CancelPopupBU" onClick={(e) => setModalShow(false)}>
            Cancel
          </button>
          <button type="submit" className="SubmitPopupBU">
            {isEdit ? "Update Refund" : "Record Refund"} · {money(refundAmount)}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RecordRefund;
